import React, { useState, useEffect, FC } from 'react';
import s from './QuantityCards.module.scss';
import { usewpdataSelectors } from 'lib/wpdataState/selectors';
import Select, { Option } from 'rc-select';
import 'rc-select/assets/index.css';
import { ProductCartItem } from '@lib/cartState/types';
import { freeProduct, colorVariant, ProductVariantItem } from '@lib/productsState/productsTypes';
import BestSellerBadge from '../../Misc/BestSellerBadge';
import { useproductsdataSelectors } from '@lib/productsState/selectors';
import b from '../../Misc/Badge.module.scss';
import ss from './VariantsSelect.module.scss';
import { getItemsAmount } from '@utils/defaultVariants';

interface QuantityCardProps {
  active: boolean;
  selectedProduct: ProductCartItem | null;
  id: string;
  index: number;
  sku: string;
  konnektive_id: number;
  amount: string | number;
  price: string | number;
  old_price: string | number;
  display_name: string;
  image: string;
  is_label_enabled?: boolean;
  description?: string;
  thank_you_displayed_name: string;
  best_seller: boolean;
  best_value: boolean;
  free_product_step: number | boolean;
  generic_name: string;
  free_products: freeProduct[];
  // eslint-disable-next-line no-unused-vars
  handleSizeChange: (value: string, index: number) => void;
  // eslint-disable-next-line no-unused-vars
  handleColorChange: (value: string, index: number) => void;
  // eslint-disable-next-line no-unused-vars
  handleClick: (product: ProductCartItem) => void;
}

const QuantityCardVariants: FC<QuantityCardProps> = ({
  id,
  active,
  amount,
  price,
  old_price,
  selectedProduct,
  sku,
  konnektive_id,
  display_name,
  description,
  image,
  thank_you_displayed_name,
  best_seller,
  best_value,
  generic_name,
  free_product_step,
  free_products,
  handleClick,
  handleColorChange,
  handleSizeChange,
}) => {
  const { Settings, hero } = usewpdataSelectors();
  const { ColorVariants, SizeVariants, EnableColorVariants, EnableSizeVariants, DefaultVariant } =
    useproductsdataSelectors();

  const [formattedPrice, setFormattedPrice] = useState<string | null>(null);
  const [formattedOldPrice, setFormattedOldPrice] = useState<string | null>(null);

  const ProductPrice = (Number(price) * Number(amount)).toFixed(2);

  useEffect(() => {
    setFormattedPrice(Settings.formattedString.replace(/(0\.00|0,00|0)/, ProductPrice.toString()));
    setFormattedOldPrice(Settings.formattedString.replace(/(0\.00|0,00|0)/, old_price.toString()));
  }, [price, old_price]);

  const itemsAmount = getItemsAmount(free_products, Number(amount));

  return (
    <>
      <div
        onClick={() => {
          const product: ProductCartItem = {
            id,
            amount: Number(amount),
            price: Number(price),
            old_price: Number(old_price),
            display_name,
            thank_you_displayed_name,
            image,
            sku,
            konnektive_id,
            description: description || '',
            best_seller,
            best_value,
            type: 'offer',
            generic_name,
            free_product_step: Number(free_product_step),
            free_products,
            variants:
              EnableColorVariants || EnableSizeVariants
                ? (Array(Number(itemsAmount)).fill(DefaultVariant) as ProductVariantItem[])
                : null,
          };

          handleClick(product);
        }}
        id={id}
        style={
          {
            '--badge-best-seller': hero.styles.bestSellerColor,
            '--badge-best-value': hero.styles.bestValueColor,
            '--active-border-color': hero.styles.activeColor,
          } as React.CSSProperties & { [key: string]: string | number }
        }
        className={`${s.card} ${active ? s['card--active'] : ''}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
          }
        }}
        tabIndex={0}
        role="button"
      >
        {best_seller && (
          <div className={`${b['badge--mobile']} ${b['badge--mobile--seller']}`.trim()}>{'BEST SELLER'}</div>
        )}{' '}
        {best_value && (
          <div className={`${b['badge--mobile']} ${b['badge--mobile--value']}`.trim()}>{'BEST VALUE'}</div>
        )}
        <label htmlFor={String(amount)} className={`${s.card__content} ${active ? s['card--selected'] : ''}`}>
          <div className={s.card__leftSide}>
            <input
              className={s.card__input}
              type="radio"
              name={String(id)}
              style={{ background: hero.styles.radioColor }}
              checked={active || false}
              id={id}
            />
            <div className={s.card__customRadioBtn} style={{ borderColor: hero.styles.radioColor }}>
              <div className={s.card__customInnerRabioBtn} style={{ background: hero.styles.radioColor }} />
            </div>
            <img src={image} className={s.card__img} alt={Settings.brandName} />
            <div className={s.card__titleBox}>
              <div className={s.title} style={{ color: hero.styles.textColorPrimary }}>
                {display_name}
              </div>
              {description && (
                <div className={s.description} style={{ color: hero.styles.textColorPrimary }}>
                  {description}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            {best_seller && <BestSellerBadge text={'Best Seller'} isBestSeller={true} />}
            {best_value && <BestSellerBadge text={'Best Value'} isBestSeller={true} />}

            <div className={s.card__rightSide}>
              <div className={s.card__price}>
                <span style={{ color: hero.styles.textColorPrimary }}>{formattedPrice}</span>
                {!Settings.showEachLabel && (
                  <p className={s.card__eachText} style={{ color: hero.styles.priceColor }}>
                    /{hero.each_label}
                  </p>
                )}
              </div>
              <div className={s.discount} style={{ color: hero.styles.oldPriceColor }}>
                {formattedOldPrice}
              </div>
            </div>
          </div>
        </label>
        {active && (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label="Select variants"
          >
            <section className={ss.container}>
              {Array.from({ length: Number(itemsAmount) }).map((_, i) => (
                <div className={ss.container__row} key={i}>
                  <div className={ss.container__row__index}>{i + 1}.</div>
                  {EnableColorVariants && (
                    <Select
                      className={ss.select}
                      data-index={i}
                      value={selectedProduct?.variants?.[i]?.color}
                      onChange={(value) => handleColorChange(value, i)}
                    >
                      {ColorVariants?.map((variant, index) => (
                        <Option key={index} value={variant.sku}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <div
                              style={{
                                border: '1px solid #e0e0e0',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: variant.color_hex,
                              }}
                            ></div>
                            <span>{variant.name}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  )}
                  {EnableSizeVariants && (
                    <Select
                      className={ss.select}
                      data-index={i}
                      value={selectedProduct?.variants?.[i]?.size}
                      onChange={(value) => handleSizeChange(value, i)}
                    >
                      {SizeVariants &&
                        SizeVariants?.map((variant, index) => {
                          const blacklistedSizes = ColorVariants?.find(
                            (item: colorVariant) => item?.sku === selectedProduct?.variants?.[i]?.color
                          )?.blacklisted_sizes;

                          if (blacklistedSizes && blacklistedSizes?.split(',').includes(variant.sku)) {
                            return null;
                          }

                          return (
                            <Option key={index} value={variant.sku}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span>{variant.name}</span>
                              </div>
                            </Option>
                          );
                        })}
                    </Select>
                  )}
                </div>
              ))}
            </section>
          </div>
        )}
      </div>
    </>
  );
};

export default QuantityCardVariants;
